// eterna/frontend/src/pages/ProposalDetail/index.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft } from 'lucide-react';
import Logo from '../../components/common/Logo.tsx';
import SubmitProposalButton from '../../components/common/SubmitProposalButton.tsx';
import ProposalHeader from '../../components/proposal/ProposalHeader.tsx';
import ProposedSolution from '../../components/proposal/ProposedSolution.tsx';
import CompanyDescription from '../../components/proposal/CompanyDescription.tsx';
import ObjectivesKeyResults from '../../components/proposal/ObjectivesKeyResults.tsx';
import ProcessComparison from '../../components/proposal/ProcessComparison.tsx';
import ProfitRiskAnalysis from '../../components/proposal/ProfitRiskAnalysis.tsx';
import UseCaseComparison from '../../components/proposal/UseCaseComparison.tsx';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'rgba(26, 26, 46, 0.1)',
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(8),
}));

const Header = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  zIndex: 100,
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

// Mock data for proposals
const proposalsData = {
  '1': {
    id: '1',
    title: 'AI-Powered Customer Service Platform',
    date: '1/14/2025',
    timeline: '1 year',
    timelineType: 'Initiative',
    status: 'submitted' as const,
    description: 'Develop an intelligent customer service platform that uses AI to handle routine inquiries and escalate complex issues to human agents.',
    
    solution: {
      id: '1',
      title: 'Integrated AI Customer Service Platform',
      description: 'A comprehensive platform that combines NLP, machine learning, and a user-friendly interface to transform customer service operations.',
      cost: '$250,000',
      timeframe: '9 months',
    },
    
    company: {
      name: 'TechInnovate',
      description: 'A leading technology innovation company focused on AI solutions',
      vision: 'To transform industries through intelligent automation',
      mission: 'Deliver cutting-edge AI solutions that solve real-world problems',
    },
    
    objectives: [
      {
        id: '1',
        title: 'Market Expansion',
        description: 'Expand into new markets and industries',
        keyResults: [
          {
            id: '1-1',
            description: 'Enter 3 new industry verticals',
            progress: 33,
            target: '3/3 complete',
          },
          {
            id: '1-2',
            description: 'Achieve 25% revenue growth',
            progress: 60,
            target: '15/25 complete',
          },
        ],
      },
    ],
    
    currentProcess: {
      title: 'Current Customer Support Process',
      description: 'Manual ticket creation and routing process with human agents handling all inquiries',
      steps: [
        { id: '1', description: 'Customer submits inquiry through web form or phone' },
        { id: '2', description: 'Support ticket is created manually' },
        { id: '3', description: 'Ticket is assigned to available agent' },
        { id: '4', description: 'Agent researches and responds to customer' },
        { id: '5', description: 'Ticket is closed upon resolution' },
      ],
    },
    
    futureProcess: {
      title: 'Future AI-Powered Support Process',
      description: 'Automated initial response with AI handling routine inquiries and routing complex issues',
      steps: [
        { id: '1', description: 'Customer submits inquiry through any channel' },
        { id: '2', description: 'AI analyzes inquiry and provides immediate response for routine questions' },
        { id: '3', description: 'Complex issues are routed to appropriate human agent with AI-generated context' },
        { id: '4', description: 'Agent resolves issue with AI assistance' },
        { id: '5', description: 'System learns from resolution for future improvements' },
      ],
    },
    
    currentFinancials: [
      {
        id: '1',
        description: 'High customer support costs due to large support team',
        amount: -500000,
        type: 'risk' as const,
      },
      {
        id: '2',
        description: 'Customer churn due to slow response times',
        amount: -750000,
        type: 'risk' as const,
      },
    ],
    
    futureFinancials: [
      {
        id: '1',
        description: 'Reduced support costs through automation',
        amount: 400000,
        type: 'profit' as const,
      },
      {
        id: '2',
        description: 'Increased customer retention through improved satisfaction',
        amount: 900000,
        type: 'profit' as const,
      },
    ],
    
    currentUseCases: [
      {
        id: '1',
        title: 'Password Reset Assistance',
        description: 'Customer needs help resetting their account password',
      },
      {
        id: '2',
        title: 'Product Feature Inquiries',
        description: 'Customer has questions about specific product features',
      },
    ],
    
    futureUseCases: [
      {
        id: '1',
        title: 'Proactive Service Notifications',
        description: 'System detects potential issues and notifies customers before they experience problems',
      },
      {
        id: '2',
        title: 'Personalized Product Recommendations',
        description: 'AI analyzes customer history and provides tailored product suggestions during support interactions',
      },
    ],
  },
  '2': {
    id: '2',
    title: 'Predictive Maintenance System',
    date: '1/31/2025',
    timeline: '3 months',
    timelineType: 'Epic',
    status: 'in-review' as const,
    description: 'Create a system that predicts equipment failures before they occur using machine learning and IoT sensors.',
    
    solution: {
      id: '2',
      title: 'IoT-Enabled Predictive Maintenance Platform',
      description: 'An advanced system that combines IoT sensor data with machine learning algorithms to predict equipment failures and schedule maintenance before breakdowns occur.',
      cost: '$180,000',
      timeframe: '3 months',
    },
    
    company: {
      name: 'TechInnovate',
      description: 'A leading technology innovation company focused on AI solutions',
      vision: 'To transform industries through intelligent automation',
      mission: 'Deliver cutting-edge AI solutions that solve real-world problems',
    },
    
    objectives: [
      {
        id: '1',
        title: 'Market Expansion',
        description: 'Expand into new markets and industries',
        keyResults: [
          {
            id: '1-1',
            description: 'Enter 3 new industry verticals',
            progress: 33,
            target: '1/3 complete',
          },
          {
            id: '1-2',
            description: 'Achieve 25% revenue growth',
            progress: 60,
            target: '15/25 complete',
          },
        ],
      },
    ],
    
    currentProcess: {
      title: 'Current Maintenance Process',
      description: 'Scheduled maintenance based on fixed intervals or reactive maintenance after failures',
      steps: [
        { id: '1', description: 'Equipment is maintained on a fixed schedule regardless of condition' },
        { id: '2', description: 'Technicians perform routine inspections and maintenance' },
        { id: '3', description: 'Equipment failures occur unexpectedly between maintenance intervals' },
        { id: '4', description: 'Emergency repairs are conducted after failures' },
        { id: '5', description: 'Production downtime occurs during repairs' },
      ],
    },
    
    futureProcess: {
      title: 'Future Predictive Maintenance Process',
      description: 'Data-driven maintenance scheduling based on real-time equipment condition',
      steps: [
        { id: '1', description: 'IoT sensors continuously monitor equipment conditions' },
        { id: '2', description: 'ML algorithms analyze data to predict potential failures' },
        { id: '3', description: 'System generates maintenance alerts before failures occur' },
        { id: '4', description: 'Maintenance is scheduled during planned downtime' },
        { id: '5', description: 'System learns from maintenance outcomes to improve predictions' },
      ],
    },
    
    currentFinancials: [
      {
        id: '1',
        description: 'Unplanned downtime costs due to equipment failures',
        amount: -350000,
        type: 'risk' as const,
      },
      {
        id: '2',
        description: 'Emergency repair costs and overtime labor',
        amount: -220000,
        type: 'risk' as const,
      },
    ],
    
    futureFinancials: [
      {
        id: '1',
        description: 'Reduced downtime through planned maintenance',
        amount: 280000,
        type: 'profit' as const,
      },
      {
        id: '2',
        description: 'Extended equipment lifespan through optimized maintenance',
        amount: 150000,
        type: 'profit' as const,
      },
    ],
    
    currentUseCases: [
      {
        id: '1',
        title: 'Scheduled Maintenance',
        description: 'Regular maintenance performed at fixed intervals regardless of equipment condition',
      },
      {
        id: '2',
        title: 'Reactive Repairs',
        description: 'Emergency repairs conducted after equipment failures occur',
      },
    ],
    
    futureUseCases: [
      {
        id: '1',
        title: 'Condition-Based Maintenance Alerts',
        description: 'System monitors equipment health and alerts when maintenance is needed',
      },
      {
        id: '2',
        title: 'Failure Prediction and Prevention',
        description: 'ML algorithms predict potential failures weeks in advance, allowing for planned interventions',
      },
    ],
  }
};

const ProposalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<any>(null);

  useEffect(() => {
    // Check if this is a problem proposal from localStorage
    const localProposal = localStorage.getItem(`proposal-${id}`);
    
    if (localProposal) {
      // If found in localStorage, use that
      setProposal(JSON.parse(localProposal));
    } else {
      // Otherwise, use the mock data
      const proposalData = proposalsData[id as keyof typeof proposalsData];
      
      if (!proposalData) {
        // If no proposal found, redirect to home
        navigate('/');
        return;
      }
      
      setProposal(proposalData);
    }
  }, [id, navigate]);

  if (!proposal) {
    return <div>Loading...</div>;
  }

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Header>
        <Logo size="medium" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SubmitProposalButton text="New Proposal" />
          <IconButton 
            onClick={handleBack}
            sx={{ 
              color: '#8a2be2',
              '&:hover': { 
                background: 'rgba(138, 43, 226, 0.1)' 
              }
            }}
          >
            <ArrowLeft />
          </IconButton>
        </Box>
      </Header>

      <PageContainer>
        <Container maxWidth="lg">
          <ProposalHeader
            title={proposal.title}
            date={proposal.date}
            timeline={proposal.timeline}
            timelineType={proposal.timelineType}
            status={proposal.status}
            description={proposal.description}
          />

          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ProposedSolution
                title={proposal.solution.title}
                description={proposal.solution.description}
                cost={proposal.solution.cost}
                timeframe={proposal.solution.timeframe}
                solutionId={proposal.solution.id}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CompanyDescription
                companyName={proposal.company.name}
                description={proposal.company.description}
                vision={proposal.company.vision}
                mission={proposal.company.mission}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ObjectivesKeyResults objectives={proposal.objectives} />
            </Grid>

            <Grid item xs={12}>
              <ProcessComparison
                currentTitle={proposal.currentProcess.title}
                currentDescription={proposal.currentProcess.description}
                currentSteps={proposal.currentProcess.steps}
                futureTitle={proposal.futureProcess.title}
                futureDescription={proposal.futureProcess.description}
                futureSteps={proposal.futureProcess.steps}
              />
            </Grid>

            <Grid item xs={12}>
              <ProfitRiskAnalysis
                currentItems={proposal.currentFinancials}
                futureItems={proposal.futureFinancials}
              />
            </Grid>

            <Grid item xs={12}>
              <UseCaseComparison
                currentUseCases={proposal.currentUseCases}
                futureUseCases={proposal.futureUseCases}
              />
            </Grid>
          </Grid>
        </Container>
      </PageContainer>
    </>
  );
};

export default ProposalDetail;